import { createElement, type ElementType, type ReactNode } from 'react';
import { cn } from '@/lib/utils.ts';

/**
 * Typography primitives — the app's canonical text vocabulary.
 *
 * Sizes, weights, tracking and leading are codified HERE (encoding the
 * conventions already established across the app) rather than re-typed as
 * loose utility strings at every call site. This keeps headings/body/labels
 * visually consistent and makes a future type-scale change a one-file edit.
 *
 * Each primitive renders a sensible default element (e.g. `Display` → <h1>),
 * overridable via `as` for correct document semantics, and accepts `className`
 * to extend or override styles (later classes win via `cn`/tailwind-merge).
 *
 * Numeric sizing intentionally rides on Tailwind's built-in scale
 * (`text-sm` = 0.875rem, etc.) — we layer *semantics* on top, not a parallel
 * scale.
 */

type TypographyProps = {
  children: ReactNode;
  className?: string;
  /** Override the rendered element for correct semantics (e.g. `as="h2"`). */
  as?: ElementType;
} & Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'children'>;

/**
 * Builds a presentational text component bound to a default element + base
 * classes. Stays a pure, prop-driven "dumb" component (no side effects).
 */
function createText(defaultAs: ElementType, base: string, displayName: string) {
  const Component = ({ as, className, children, ...rest }: TypographyProps) =>
    createElement(
      as ?? defaultAs,
      { className: cn(base, className), ...rest },
      children
    );
  Component.displayName = displayName;
  return Component;
}

/**
 * Page-level hero title. Largest, tightest tracking — one per view.
 * Tracking is also reinforced by the global `h1`/`.text-display` base rule.
 */
export const Display = createText(
  'h1',
  'text-2xl font-semibold leading-tight tracking-[-0.015em] text-foreground',
  'Display'
);

/** Section heading — groups of related content within a page. */
export const Heading = createText(
  'h2',
  'text-lg font-semibold leading-7 tracking-[-0.01em] text-foreground',
  'Heading'
);

/** Card / panel title — the heaviest text inside a contained surface. */
export const Title = createText(
  'h3',
  'text-base font-semibold leading-6 text-foreground',
  'Title'
);

/** Default reading copy. */
export const Body = createText(
  'p',
  'text-sm font-medium leading-5 text-foreground',
  'Body'
);

/**
 * Secondary reading copy — descriptions, helper text users actually read.
 * Uses the AAA-tuned `foreground-secondary` (not the decorative-only
 * `muted-foreground`).
 */
export const Lead = createText(
  'p',
  'text-sm font-medium leading-5 text-foreground-secondary',
  'Lead'
);

/** Small label / caption — field labels, metadata, timestamps. */
export const Caption = createText(
  'span',
  'text-xs font-medium leading-4 text-foreground-secondary',
  'Caption'
);

/**
 * Decorative muted text — purely decorative labels (AA contrast). For copy
 * users read, prefer {@link Lead} / {@link Caption} which use the AAA token.
 */
export function Muted({ as, className, children, ...rest }: TypographyProps) {
  return createElement(
    as ?? 'div',
    {
      className: cn('text-muted-foreground text-xs leading-4', className),
      ...rest,
    },
    children
  );
}
